'use client';

import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';

import { TrendChart, type ChartDataPoint } from '../../../../../shared/trend-chart';
import { buildHistoryChartData } from '../../../chart-data';
import { useProductConfig } from '../../product-config';

export const AprChart = () => {
  const { detailsAtom } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  const todayApr = Number(details?.estimateApr ?? '0');
  const chartData = useMemo<ChartDataPoint[]>(
    () => buildHistoryChartData(details?.estimateAprHistory ?? [], (item) => Number(item.estimateApr), 7, todayApr),
    [details?.estimateAprHistory, todayApr],
  );

  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null);

  const handleHover = useCallback((point: ChartDataPoint | null) => {
    setHoveredPoint(point);
  }, []);

  const displayValue = hoveredPoint?.value ?? Number(details?.estimateApr ?? '0');

  const formatYAxisLabel = useCallback((value: number) => `${(value * 100).toFixed(2)}%`, []);

  return (
    <>
      <div className={hstack({ justify: 'space-between' })}>
        <div className={stack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Common.r251209.Apr' />
          </p>
          <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
            <Formatted.Percent value={displayValue} isRate precision={2} toFixed />
          </p>
        </div>
        <p
          className={center({
            direction: 'row',
            py: 8,
            px: 12,
            rounded: 1000,
            bg: 'background.accent.secondary.tint',
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-5',
          })}
        >
          <FormattedMessage id='Common.r251107.days_days' values={{ days: 7 }} />
        </p>
      </div>

      <div className={css({ h: 200, tabletDown: { h: 160 } })}>
        <TrendChart data={chartData} onHover={handleHover} formatYAxisLabel={formatYAxisLabel} />
      </div>

      <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-1', textAlign: 'right' })}>
        <FormattedMessage
          id='Common.r251107.Latest_update_colon_date_time'
          values={{
            dateTime: (
              <Datetime
                key='dateTime'
                value={details?.estimateAprLatestUpdate}
                format='yyyy.MM.dd, HH:mm'
                isUTC
                withUTCText
              />
            ),
          }}
        />
      </p>
    </>
  );
};
